// The built-in tours the Tutorial launcher offers under "Cortex tutorials". A tour is data: steps
// with a Markdown body, the `data-tour` anchor to spotlight (nav items carry `nav-<page>`, groups
// `nav-group-<name>`, the routed page `page`) and the path to open first. A project's own tours come
// from its guides (@cortex/sdk-guides-react tourFromGuide).
import type { TourListing, TourStep } from '@cortex/sdk-guides-react';

export type { TourListing };

const step = (
  id: string,
  title: string,
  body: string,
  anchor: string | null = null,
  path: string | null = null,
): TourStep => ({ id, title, body, anchor, path });

/** "What is Cortex?" — the product walkthrough every project gets. */
export function coreTour(where: { slug: string; branch: string }): TourListing {
  const at = (page: string) =>
    `/p/${encodeURIComponent(where.slug)}/b/${encodeURIComponent(where.branch)}/${page}`;
  return {
    description:
      'The product tour: projects and branches, the data dictionary, the pipeline, apps, the Board and the assistant.',
    tour: {
      id: 'core',
      title: 'What is Cortex?',
      start: 0,
      steps: [
        step(
          'welcome',
          'Welcome to Cortex',
          'Cortex is a **data-governance studio**. You describe your data as a **recipe** — the fields you care about, where the raw data lives, how it is cleaned and checked, and where the governed result lands — and the engine runs it as a pipeline.\n\nEvery change is a **commit** in the project’s own versioned database. Use **Next / Back** or the arrow keys; **Esc** leaves the tour.',
        ),
        step(
          'project',
          'Projects and branches',
          'Everything belongs to the **active project**, shown here with the **branch** you are editing. Branches work like git: experiment on one, compare it, merge it back. The URL carries both, so links are shareable.',
          'project-branch-chip',
        ),
        step(
          'dashboard',
          'Dashboard — the project at a glance',
          'How many elements are defined, which sources are connected and mapped, quality signals and recent runs. When something needs attention, it shows here first.',
          'page',
          at('dashboard'),
        ),
        step(
          'governance',
          'Data Governance — the data dictionary',
          'The heart of the recipe: **Elements** (your canonical fields), **Sources**, **Source Mappings**, **Reference Lists**, **Processing Rules**, **Data Review** and **Quality**, in the order you would build a project.',
          'nav-group-data-governance',
        ),
        step(
          'elements',
          'Elements — your canonical fields',
          'An element is one governed business field: its name, type and definition. Source columns are mapped *into* elements, rules are attached *to* them and targets receive columns *from* them.',
          'page',
          at('data-elements'),
        ),
        step(
          'operations',
          'Operations — run and version it',
          'The **Pipeline** runs the recipe, **Structure** checks every hop without moving data, **Branches** and **Versions** hold the history — any row, table or the whole design can be restored.',
          'nav-group-operations',
        ),
        step(
          'apps',
          'Apps — what your users open',
          'Apps are built on the governed data. Each app can offer **companions** — the chat, workflows, guides and more — from the button in its corner.',
          'nav-apps',
          at('apps'),
        ),
        step(
          'board',
          'The Board — ask for work',
          'Requests to build or change something land on the **Board** as cards; you follow them there until they are done.',
          'my-requests',
        ),
        step(
          'search',
          'Search anything',
          'Press **⌘K** (Ctrl+K) anywhere to find an element, a source, a rule or a page.',
          'nav-search',
        ),
        step(
          'tutorial',
          'Come back any time',
          'This button reopens the tutorials — this tour and the project’s own guides. That’s the tour!',
          'nav-tutorial',
        ),
      ],
    },
  };
}
